import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, Sparkles, Loader2 } from "lucide-react";
import { AttachmentPicker } from "@/components/Attachments";

const LANGS = [
  { code: "es", label: "Español" },
  { code: "en", label: "English" },
  { code: "it", label: "Italiano" },
];

export default function EmailComposer({ open, onClose, trip, booking, provider, multi = false, bookings = [] }) {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [language, setLanguage] = useState("es");
  const [selectedTplId, setSelectedTplId] = useState("");
  const [to, setTo] = useState(provider?.email || "");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [attachments, setAttachments] = useState([]);

  // En modo multi, filtramos por el tipo dominante (mayoría), o nos quedamos con todos los del idioma.
  const filtered = useMemo(() => {
    if (multi) {
      // tipo dominante de las reservas
      const counts = {};
      bookings.forEach((b) => { counts[b.service_type] = (counts[b.service_type] || 0) + 1; });
      const dominant = Object.entries(counts).sort((a,b) => b[1]-a[1])[0]?.[0];
      return templates.filter((t) => t.language === language && (dominant ? t.type === dominant : true));
    }
    return templates.filter((t) => t.type === booking?.service_type && t.language === language);
  }, [templates, booking, bookings, language, multi]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/templates");
      setTemplates(data);
    })();
  }, []);

  useEffect(() => {
    // Al cambiar idioma, intentamos seleccionar una plantilla por defecto SOLO si existe en ese idioma.
    // Si no existe, dejamos el cuerpo intacto (importante para no borrar tras "Traducir con IA").
    if (filtered.length > 0) {
      const def = filtered.find((t) => t.is_default) || filtered[0];
      setSelectedTplId(def.id);
    } else {
      setSelectedTplId("");
    }
  }, [filtered.length, language]); // eslint-disable-line

  useEffect(() => {
    if (!selectedTplId) return;
    (async () => {
      setRendering(true);
      try {
        if (multi) {
          const { data } = await api.post("/render-multi", {
            template_id: selectedTplId,
            booking_line_ids: bookings.map((b) => b.id),
          });
          setSubject(data.subject);
          setBody(data.body);
          if (data.to_email && !to) setTo(data.to_email);
        } else {
          const { data } = await api.post("/render", {
            template_id: selectedTplId,
            booking_line_id: booking.id,
          });
          setSubject(data.subject);
          setBody(data.body);
          if (data.to_email && !to) setTo(data.to_email);
        }
      } catch (e) {
        toast.error("Error al renderizar plantilla");
      } finally {
        setRendering(false);
      }
    })();
  }, [selectedTplId]); // eslint-disable-line

  const onTranslate = async () => {
    if (!body) return;
    setTranslating(true);
    try {
      const targets = ["es", "en", "it"].filter((l) => l !== language);
      // simple: ask user via prompt
      const target = window.prompt(`Traducir a (${targets.join(" / ")}):`, targets[0]);
      if (!target || !targets.includes(target)) {
        setTranslating(false);
        return;
      }
      const { data } = await api.post("/translate", {
        subject, body, source_lang: language, target_lang: target,
      });
      const newBody = (data.body || "").trim();
      const newSubject = (data.subject || "").trim();
      if (!newBody) {
        toast.error("La traducción devolvió un mensaje vacío. Vuelve a intentarlo.");
        return;
      }
      setSubject(newSubject || subject);
      setBody(newBody);
      setLanguage(target);
      toast.success("Traducción aplicada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al traducir");
    } finally {
      setTranslating(false);
    }
  };

  const onSend = async () => {
    if (!to || !subject || !body) {
      toast.error("Completa destinatario, asunto y cuerpo");
      return;
    }
    setSending(true);
    try {
      if (multi) {
        const { data } = await api.post("/emails/send-multi", {
          trip_id: trip.id,
          booking_line_ids: bookings.map((b) => b.id),
          provider_id: provider?.id || null,
          to_email: to,
          cc: cc || null,
          subject,
          body,
          attachment_ids: attachments.map((a) => a.file_id),
        });
        toast.success(`Correo en envío · las ${data.bookings_count} reservas se marcarán CONTACTO al completarse`);
      } else {
        await api.post("/emails/send", {
          trip_id: trip.id,
          booking_line_id: booking.id,
          provider_id: provider?.id || null,
          to_email: to,
          cc: cc || null,
          subject,
          body,
          attachment_ids: attachments.map((a) => a.file_id),
        });
        toast.success("Correo en envío — la reserva se marcará automáticamente al completarse");
      }
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al enviar");
    } finally {
      setSending(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose(false)}>
      <SheetContent side="right" className="!max-w-[1100px] w-full sm:!max-w-[1100px] p-0 flex flex-col">
        <SheetHeader className="px-6 py-4 border-b border-[var(--vv-border)]">
          <SheetTitle className="text-base">{multi ? `Componer correo conjunto · ${bookings.length} reservas` : "Componer correo de reserva"}</SheetTitle>
          <SheetDescription className="text-xs">
            {multi
              ? "Un solo correo agrupa todos los servicios seleccionados para el mismo proveedor. Al enviarlo, se marcará CONTACTO en gestion para todas las reservas."
              : "Plantilla precargada con los datos del itinerario. Revisa antes de enviar."}
          </SheetDescription>
          <div className="text-[11px] mt-1">
            {user?.smtp_connected ? (
              <span className="text-emerald-700">Se enviará desde <strong>{user.smtp_email}</strong> (real)</span>
            ) : user?.gmail_connected ? (
              <span className="text-emerald-700">Se enviará desde <strong>{user.gmail_linked_email}</strong> (Gmail API)</span>
            ) : (
              <span className="text-orange-700">Sin correo conectado · el envío será simulado (se guardará en Correos enviados). Conéctalo desde la barra lateral.</span>
            )}
          </div>
        </SheetHeader>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 overflow-hidden">
          {/* Editor */}
          <div className="p-6 space-y-4 overflow-y-auto" data-testid="email-composer-editor">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="vv-label">Idioma</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger data-testid="composer-language-select"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {LANGS.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="vv-label">Plantilla</Label>
                <Select value={selectedTplId} onValueChange={setSelectedTplId}>
                  <SelectTrigger data-testid="composer-template-select">
                    <SelectValue placeholder={filtered.length ? "Selecciona…" : "Sin plantillas para este idioma/tipo"}/>
                  </SelectTrigger>
                  <SelectContent>
                    {filtered.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="vv-label">Para</Label>
              <Input value={to} onChange={(e) => setTo(e.target.value)} data-testid="composer-to-input"/>
            </div>
            <div>
              <Label className="vv-label">CC (opcional)</Label>
              <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="copia@..."/>
            </div>
            <div>
              <Label className="vv-label">Asunto</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="composer-subject-input"/>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="vv-label">Mensaje</Label>
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs"
                  onClick={onTranslate} disabled={translating || !body}
                  data-testid="composer-translate-button">
                  {translating ? <Loader2 size={12} className="mr-1 animate-spin"/> : <Sparkles size={12} className="mr-1"/>}
                  Traducir con IA
                </Button>
              </div>
              <Textarea rows={18} value={body} onChange={(e) => setBody(e.target.value)} className="font-mono text-[13px] leading-relaxed mt-1" data-testid="composer-body-textarea"/>
              {rendering && <p className="text-[11px] text-gray-400 mt-1"><span className="vv-dots"><span>·</span><span>·</span><span>·</span></span> renderizando plantilla</p>}
            </div>
            <div>
              <Label className="vv-label">Adjuntos (opcional)</Label>
              <div className="mt-1">
                <AttachmentPicker attachments={attachments} onChange={setAttachments} testIdPrefix="composer-att" />
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="preview-pane overflow-y-auto hidden lg:block" data-testid="email-composer-preview">
            <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-2">Vista previa</div>
            <div className="bg-white border border-[var(--vv-border)] rounded-md p-5">
              <div className="text-xs text-gray-500 mb-1">Para: <span className="text-gray-800">{to || "—"}</span></div>
              {cc && <div className="text-xs text-gray-500 mb-1">CC: <span className="text-gray-800">{cc}</span></div>}
              <div className="text-base font-semibold tracking-tight mb-4">{subject || "—"}</div>
              <pre className="font-sans text-[13px] leading-relaxed whitespace-pre-wrap text-gray-800">{body || "—"}</pre>
              {attachments.length > 0 && (
                <div className="mt-3 pt-2 border-t border-gray-100 text-[11px] text-gray-500">
                  📎 {attachments.length} adjunto{attachments.length > 1 ? "s" : ""}: {attachments.map((a) => a.filename).join(", ")}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-[var(--vv-border)] px-6 py-3 flex items-center justify-end gap-2 bg-white">
          <Button variant="ghost" onClick={() => onClose(false)} disabled={sending}>Cancelar</Button>
          <Button onClick={onSend} disabled={sending} className="bg-blue-600 hover:bg-blue-700" data-testid="composer-send-button">
            {sending ? <Loader2 size={14} className="mr-2 animate-spin"/> : <Send size={14} className="mr-2"/>}
            Enviar correo
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
